import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";
import { _ } from "@flowable/forms";

import { getAllFieldsForType, formatNumber } from "utils";

const isNumeric = value => {
	return value !== "" && !isNaN(parseFloat(value)) && isFinite(value);
};

const normalizeNumber = value => {
	if (value === undefined || value === "") {
		return 0;
	} else if (typeof value === "string" && isNumeric(value)) {
		return parseFloat(value);
	}

	return value;
};

// Fallback flw utilities matching Flowable's built-in expression functions.
// These are only injected when flw is absent from the scope (i.e. the component
// is rendered outside a full Flowable Form context).
const FLW_DEFAULTS = {
	// Array functions
	sum: arr => (arr == null || !isNonEmptyArray(arr)) ? null : arr.length === 0 ? 0 : arr.reduce((acc, v) => acc + (+v || 0), 0),
	avg: arr => (arr == null || !isNonEmptyArray(arr)) ? null : arr.length === 0 ? 0 : FLW_DEFAULTS.sum(arr) / arr.length,
	count: arr => (arr == null || !isNonEmptyArray(arr)) ? null : arr.length,
	min: arr => (!isNonEmptyArray(arr)) ? null : arr.reduce((a, b) => Math.min(a, +b || Number.MIN_VALUE), Number.MAX_VALUE),
	max: arr => (!isNonEmptyArray(arr)) ? null : arr.reduce((a, b) => Math.max(a, +b || Number.MIN_VALUE), Number.MIN_VALUE),
	join: (arr, sep = ",") => (!isNonEmptyArray(arr)) ? null : arr.join(sep),
	// Math functions
	abs: v => Math.abs(v),
	round: v => Math.round(v),
	floor: v => Math.floor(v),
	ceil: v => Math.ceil(v),
	parseInt: v => parseInt(v),
	parseFloat: v => parseFloat(v),
};

/**
 * Preprocess scope to ensure all number fields are defined for expression evaluation
 * (undefined or empty string are set to 0, string numbers are converted to actual numbers).
 * Also injects flw utility functions when missing from the scope.
 * @param scope
 * @param form
 * @returns scopeCopy - processed scope with all number fields defined
 */
export const preprocessScope = (scope, form) => {
	const scopeCopy = { ...scope };
	const allNumberFields = getAllFieldsForType(form, [ "number", "one" ]) ?? [];
	const allDataTableFields = getAllFieldsForType(form, [ "dataTable" ]) ?? [];

	allNumberFields.forEach(key => {
		scopeCopy[key] = normalizeNumber(scopeCopy[key]);
	});

	allDataTableFields.forEach(key => {
		if (scopeCopy[key] && isNonEmptyArray(scopeCopy[key].rows)) {
			const values = {};
			scopeCopy[key].rows.forEach(row => {
				if (row && isNonEmptyArray(row.columns)) {
					row.columns.forEach(column => {
						values[column.name] = [ ...values[column.name] || [], column.value ];
					});
				}
			});
			scopeCopy[key] = { ...scopeCopy[key], ...values };
		}
	});

	if (!scopeCopy.flw) {
		scopeCopy.flw = FLW_DEFAULTS;
	}

	return scopeCopy;
};

export const evaluateExpression = (expression, scope) => {
	try {
		return _.evalExpression(expression, scope);
	} catch (e) {
		console.error("Failed to evaluate expression:", expression, e);

		return "";
	}
};


export const parseAndFormatExpression = (value, config, lang) => {
	const numeric = isNumeric(value);

	return {
		isNumeric: numeric,
		expressionValue: numeric ? parseFloat(value) : value || "",
		formattedValue: numeric
			? formatNumber({ ...config, expression: value, value }, lang)
			: value || ""
	};
};

export const toNum = v => {
	if (v == null) {
		return NaN;
	}
	if (typeof v === "string" && v.trim() === "") {
		return NaN;
	}
	const n = Number(v);

	return Number.isFinite(n) ? n : NaN;
};

export const valuesEqual = (modelVal, exprVal) => {
	const a = toNum(modelVal);
	const b = toNum(exprVal);
	if (!Number.isNaN(a) && !Number.isNaN(b)) {
		return a === b;
	}
	if ((exprVal == null || exprVal === "") && (modelVal === 0 || modelVal === "0")) {
		return true;
	}

	return String(modelVal ?? "") === String(exprVal ?? "");
};

export const coerceToModelType = (rawModelVal, exprVal) => {
	const a = toNum(rawModelVal);
	const b = toNum(exprVal);
	const bothNumeric = !Number.isNaN(a) && !Number.isNaN(b);

	if (typeof rawModelVal === "string") {
		return String(exprVal ?? "");
	} else {
		if (bothNumeric) {
			return b;
		} else {
			return exprVal ?? "";
		}
	}
};
